import React from 'react';
import { BLOCKS } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

/* Constants */
import { Rules } from '@integrations/contentful/contentful.constants';

/* Types */
import { Document, Options } from '@integrations/contentful/contentful.types';
import { IHash } from 'types/base.types';
/**
 * This will read the data and look for {{*}} and substitute the mapped value.
 * Assumptions are: User inputed proper key:
 *    city, state, serviceName
 *    variants:
 *      or: value|value,
 *      and: value,value - add comma, space between
 */

/**
 * Simple helper to reduce repetition
 * @param stringMatch
 * @param rule
 * @param mapper
 * @returns {any}
 * @constructor
 */
const SplitAndMap = (stringMatch: string, rule: Rules, mapper: IHash<string>) =>
  stringMatch.split(rule).map(v => {
    const trim = v.trim();

    return mapper[trim];
  });

/**
 * This takes in contents (ie.. title, paragaph etc.. any string). Then does the
 * substitutions. We expect {{lowercase}} for the thing to be substituted.
 *
 * @param {string} contents
 * @param {{}} mapper
 * @returns {string}
 */
export const substituteMappedValues = (contentString = '', mapper = {}) => {
  const regex = /\{{(.*?)\}}/gi;
  const contents = contentString || '';

  const updatedContents = contents.replace(regex, match => {
    let matchedEntry = match.replace(regex, '$1');

    /* AND */
    if (matchedEntry.includes(Rules.AND)) {
      matchedEntry = SplitAndMap(matchedEntry, Rules.AND, mapper).filter(Boolean).join(', ');

      return matchedEntry;
    }

    /* OR */
    if (matchedEntry.includes(Rules.OR)) {
      matchedEntry = SplitAndMap(matchedEntry, Rules.OR, mapper).find(Boolean) || '';

      return matchedEntry;
    }

    /* Because we don't want {{something}} in code if we do not find match, return empty string */
    return (mapper as Record<string, string>)[matchedEntry] || '';
  });

  return updatedContents;
};

/* Display Types */
const Text = ({ children }: { children: React.ReactNode }) => <p className="p1 htBlue700 paddingBottom-small1">{children}</p>;

/**
 * This controls how the ui will behave. Add more as you please, check Contentful Docs.
 * @param {IHash<string>} mapper
 * @returns {{renderNode: {}; renderText: (text: string) => string}}
 */
const contentfulOptions: (arg: IHash<string>) => Options = mapper => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
  },
  renderText: (text: string) => substituteMappedValues(text, mapper),
});

/**
 * Takes in the dynamic content and mapping for the substitutions.
 * @param {Document} doc
 * @param {IHash<string>} mapper
 * @returns {any}
 */
export const renderer = (doc: Document, mapper: IHash<string>) => {
  if (!doc) return '';
  return documentToReactComponents(doc, contentfulOptions(mapper));
};
